import {
  resolveDisplayStatusTier1,
  type DisplayStatusSortKey,
  type DisplayStatusTier1,
} from '@talleflow/document-display-status';

import type { DocumentDisplayStatus } from './documentPageTypes';

const VALID_TIER1 = new Set<DisplayStatusTier1>(['draft', 'in_progress', 'completed']);

const VALID_VARIANTS = new Set<DocumentDisplayStatus['variant']>([
  'draft',
  'sent',
  'viewed',
  'in_progress',
  'client_signed',
  'paid',
  'partially_paid',
  'signed',
  'completed',
  'submitted',
  'declined',
  'failed',
  'voided',
  'expired',
  'payment_failed',
  'payment_voided',
]);

/** Maps raw API `displayStatus` onto the normalized view-model shape. */
export function mapDisplayStatus(raw: unknown): DocumentDisplayStatus | undefined {
  if (raw === null || raw === undefined) {
    return undefined;
  }
  if (typeof raw !== 'object') {
    return undefined;
  }

  const value = raw as Record<string, unknown>;
  const variant = value.variant;
  if (
    typeof value.label !== 'string' ||
    typeof variant !== 'string' ||
    typeof value.sortKey !== 'string' ||
    !VALID_VARIANTS.has(variant as DocumentDisplayStatus['variant'])
  ) {
    return undefined;
  }

  const typedVariant = variant as DocumentDisplayStatus['variant'];
  const tier1 =
    mapDisplayStatusTier1(value.tier1) ??
    resolveDisplayStatusTier1({
      sortKey: value.sortKey as DisplayStatusSortKey,
      variant: typedVariant,
    });

  return {
    label: value.label,
    variant: typedVariant,
    sortKey: value.sortKey,
    ...(typeof value.detail === 'string' ? { detail: value.detail } : {}),
    tier1,
  };
}

/** Maps optional API `displayStatus.tier1` when both `tier` and `label` are valid. */
function mapDisplayStatusTier1(raw: unknown): DocumentDisplayStatus['tier1'] | undefined {
  if (raw === null || raw === undefined || typeof raw !== 'object') {
    return undefined;
  }

  const value = raw as Record<string, unknown>;
  if (typeof value.tier !== 'string' || typeof value.label !== 'string') {
    return undefined;
  }
  if (!VALID_TIER1.has(value.tier as DisplayStatusTier1)) {
    return undefined;
  }

  return {
    tier: value.tier as DisplayStatusTier1,
    label: value.label,
  };
}
